import React from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';

import NavigationTab from '@travel/traveler-core/components/NavigationTab';
import { useDeviceType, useTranslation } from '@travel/traveler-core/hooks';

import DataLayer from 'components/DataLayer';
import { TAB_ID } from 'constants/providerInfoTabIds';
import paths from 'core/universalRouter/paths';
import { getItem as getProviderItem } from 'store/providerInformation/selectors';
import { getProviderGuestReview } from 'store/providerReview/selectors';
import ProviderInfo from './components/ProviderInfo';
import ProviderReviews from './components/ProviderReviews';

import { pushLocation } from 'store/__router/actions';

import { ProviderSEOReviewList } from 'ProviderReview-Types';
import { roundReviewScore } from 'utils/review';

// using the same style with the ProviderInformation page
import styles from '../ProviderInformationPage/providerInformation.module.scss';

export const generateProviderGuestReviewJsonLd = (reviewScoreInfo: ProviderSEOReviewList) => {
  const { provider = {}, averageScore, total } = reviewScoreInfo;
  return {
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    name: reviewScoreInfo?.provider.name || '',
    address: {
      '@type': 'PostalAddress',
      postalCode: provider.address?.postCode || '',
      streetAddress: provider.address?.addressLine || '',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: averageScore ? roundReviewScore(averageScore) : '',
      ratingCount: total || '',
    },
  };
};

type Props = {
  params: { pageIdentifier: string; pathId: string };
};

function ProviderGuestReviewPage(props: Props) {
  const dispatch = useDispatch();
  const provider = useSelector(getProviderItem);
  const reviewScoreInfo = useSelector(getProviderGuestReview);

  const providerInfoUrl = paths.providerInfo.pathResolver(provider.pathId);

  const isSP = useDeviceType() === 'sp';
  const reviewList = (
    <ProviderReviews pageIdentifier={props.params.pageIdentifier} className={styles.contentBox} />
  );

  const roomListLabel = useTranslation({ id: 'Provider_Info.Menu.Room_List_Tab' });
  const planListLabel = useTranslation({ id: 'Provider_Info.Menu.Plan_List_Tab' });
  const basicInfoLabel = useTranslation({ id: 'Provider_Info.Menu.Basic_Info' });
  const reviewInfoLabel = useTranslation({ id: 'Provider_Info.Menu.Reviews' });
  const highlightsLabel = useTranslation({ id: 'Provider_Info.Menu.Customized' });

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(generateProviderGuestReviewJsonLd(reviewScoreInfo))}
        </script>
      </Helmet>
      <div data-locator-id="ProviderGuestReviewPage-543d6c6a-710c-4b77-9c20-fe66d38df7f4">
        <ProviderInfo providerInfoUrl={providerInfoUrl} className={styles.contentBox} />

        {!isSP && reviewList}

        {/* mobile: tab menu */}
        {isSP && (
          <NavigationTab
            onClick={(event: React.ChangeEvent) => {
              if (event.currentTarget.id !== TAB_ID.REVIEWS) {
                dispatch(pushLocation(providerInfoUrl));
              }
            }}
            isAnimatedOnStart={false}
            selectedTab={TAB_ID.REVIEWS}
            className={styles.tab}
            tabMenuClassName={styles.tabMenu}
            tabs={[
              {
                id: TAB_ID.ROOM_LIST,
                title: roomListLabel,
                content: null,
              },
              {
                id: TAB_ID.PLAN_LIST,
                title: planListLabel,
                content: null,
              },
              {
                id: TAB_ID.INFO,
                title: basicInfoLabel,
                content: null,
              },
              {
                id: TAB_ID.REVIEWS,
                title: reviewInfoLabel,
                content: reviewList,
              },
              {
                id: TAB_ID.HIGHLIGHTS,
                title: highlightsLabel,
                content: null,
              },
            ]}
          />
        )}
        <DataLayer pageName="hotel:reviews_list" pageType="shop_item" siteSection="hotel" />
      </div>
    </>
  );
}

export default ProviderGuestReviewPage;
